/**
 * Support Page - Landlord
 * Live chat panel and contact form side by side
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChatContainer,
  MessageList,
  Message,
  Avatar,
  ConversationHeader,
} from '@chatscope/chat-ui-kit-react';
import '@/styles/chat-kit.css';
import { useAuth } from '../hooks/useAuth';
import { useSupportChat } from '../contexts/SupportChatContext';
import { TestableMessageInput } from '../components/chat/TestableMessageInput';
import { readContactHandoff } from '../components/help/contact-handoff';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CONTACT_MESSAGE_MAX_CHARS,
  CONTACT_SUBJECT_MAX_CHARS,
  ContactRequestSchema,
} from '@resida/api-types/support';

/** Initials avatar; the service picks its own neutral colors, so none are hard-coded here. */
function getAvatarUrl(name: string): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=128`;
}

const HANDOFF_NOTES = {
  attached: 'We added your Help conversation below. Edit it before sending.',
  failed: "We couldn't attach your Help conversation. Tell us what you need help with below.",
} as const;

export function SupportPage() {
  const { manager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    availability,
    messages,
    sendMessage,
    submitContactForm,
    contactFormSuccess,
    isLoading,
    error,
    activate,
  } = useSupportChat();

  // Activate support data loading + Centrifugo subscription on first visit
  useEffect(() => { activate(); }, [activate]);

  const [isSending, setIsSending] = useState(false);
  const handoff = readContactHandoff(location.state);
  const [contactSubject, setContactSubject] = useState(handoff?.kind === 'attached' ? handoff.subject : '');
  const [contactMessage, setContactMessage] = useState(handoff?.kind === 'attached' ? handoff.body : '');
  const contactFormRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  // Arriving from the help chat (also while already on this page, which changes the router state without
  // remounting) lands on the contact form: on a narrow screen the live-chat card above it would otherwise
  // be all the landlord sees.
  useEffect(() => {
    const next = readContactHandoff(location.state);
    if (!next) return;
    if (next.kind === 'attached') {
      setContactSubject(next.subject);
      setContactMessage(next.body);
    }
    const stacked = !window.matchMedia('(min-width: 768px)').matches;
    if (stacked) contactFormRef.current?.scrollIntoView({ block: 'start' });
    subjectRef.current?.focus({ preventScroll: true });
  }, [location.state]);
  const [contactError, setContactError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messageListRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = useCallback(async (content: string) => {
    if (isSending) return;
    try {
      setIsSending(true);
      await sendMessage(content);
      setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 50);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setIsSending(false);
    }
  }, [sendMessage, isSending]);

  const handleContactSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactSubject.trim() || !contactMessage.trim()) return;
    const parsed = ContactRequestSchema.safeParse({
      subject: contactSubject.trim(),
      message: contactMessage.trim(),
    });
    if (!parsed.success) {
      setContactError('Your subject or message is too long. Shorten it and try again.');
      return;
    }
    try {
      setIsSubmitting(true);
      setContactError(null);
      await submitContactForm(parsed.data.subject, parsed.data.message);
      setContactSubject('');
      setContactMessage('');
      // The handoff lives in history.state, which survives a reload: clear it so the note and draft do not return.
      navigate(location.pathname, { replace: true, state: null });
    } catch (err) {
      setContactError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  }, [contactSubject, contactMessage, submitContactForm, navigate, location.pathname]);

  const operatorName = availability?.operatorDisplayName ?? 'Support';

  const formatNextAvailable = (ts?: string): string => {
    if (!ts) return 'when we\'re back';
    try {
      return new Date(ts).toLocaleString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <div className="max-w-5xl mx-auto pb-6 px-[var(--page-padding-x)]" data-testid="support-page">
      <PageHeader title="Support" />

      {error && (
        <div
          className="p-3 mb-4 bg-destructive/10 border border-destructive rounded text-sm text-destructive"
          data-testid="support-error"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Live Chat Panel */}
        <div
          className="flex flex-col rounded-xl overflow-hidden bg-card border border-border min-h-[480px] max-h-[calc(100vh-12rem)]"
          data-testid="support-chat-panel"
        >
          {/* Availability indicator */}
          <div
            className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0"
            data-testid="support-availability-indicator"
          >
            {availability?.chatAvailable ? (
              <>
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-success"
                  aria-hidden="true"
                />
                <span className="text-sm text-foreground">
                  We're online &mdash; {operatorName} will reply shortly
                </span>
              </>
            ) : (
              <>
                <span
                  className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="text-sm text-muted-foreground">
                  We'll respond by {formatNextAvailable(availability?.nextAvailableAt)}
                </span>
              </>
            )}
          </div>

          {/* Chat area */}
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <ChatContainer style={{ flex: 1, minHeight: 0, width: '100%' }}>
                <ConversationHeader>
                  <Avatar
                    name={operatorName}
                    src={getAvatarUrl(operatorName)}
                  />
                  <ConversationHeader.Content userName={operatorName} info="Product Support" />
                </ConversationHeader>

                <MessageList
                  ref={messageListRef as unknown as React.Ref<HTMLDivElement>}
                  data-testid="support-message-list"
                >
                  {messages.length === 0 && (
                    <MessageList.Content>
                      <div className="flex flex-col items-center justify-center h-full py-8 text-center px-4">
                        <p className="text-muted-foreground text-sm">
                          Send us a message and we'll get back to you as soon as possible.
                        </p>
                      </div>
                    </MessageList.Content>
                  )}
                  {messages.map((message) => {
                    const isOwn = message.senderRole === 'USER';
                    const senderName = isOwn ? (manager?.displayName ?? 'You') : operatorName;

                    return (
                      <Message
                        key={message.id}
                        model={{
                          message: message.content,
                          sentTime: new Date(message.createdAt).toISOString(),
                          sender: senderName,
                          direction: isOwn ? 'outgoing' : 'incoming',
                          position: 'normal',
                          type: 'text',
                        }}
                        data-testid={`support-message-${message.id}`}
                      >
                        <Avatar
                          name={senderName}
                          src={getAvatarUrl(senderName)}
                        />
                      </Message>
                    );
                  })}
                </MessageList>
              </ChatContainer>
            )}

            <TestableMessageInput
              placeholder="Type a message..."
              onSend={handleSendMessage}
              disabled={isSending || isLoading}
              data-testid="support-send-button"
            />
          </div>
        </div>

        {/* Contact Form Panel */}
        <div
          ref={contactFormRef}
          className="flex flex-col scroll-mt-20 rounded-xl p-6 bg-card border border-border"
          data-testid="support-contact-form"
        >
          <h2 className="text-lg font-semibold text-foreground mb-1">Send us a message</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Fill out the form and we'll respond by email.
          </p>

          {contactFormSuccess ? (
            <p
              className="text-sm text-foreground rounded-lg px-4 py-3 bg-muted border border-border"
              data-testid="support-contact-success"
            >
              Message sent! We'll respond to {manager?.email ?? 'your email'}.
            </p>
          ) : (
            <form onSubmit={handleContactSubmit} className="flex flex-col gap-4">
              {handoff && (
                <p
                  id="support-handoff-note"
                  role="status"
                  className="rounded-lg border border-info/25 bg-info/10 p-3 text-[13px] text-foreground"
                  data-testid="support-contact-handoff-note"
                >
                  {HANDOFF_NOTES[handoff.kind]}
                </p>
              )}
              {contactError && (
                <div
                  className="p-3 bg-destructive/10 border border-destructive rounded text-sm text-destructive"
                  data-testid="support-contact-error"
                >
                  {contactError}
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="support-subject"
                  className="text-sm font-medium text-foreground"
                >
                  Subject
                </label>
                <Input
                  ref={subjectRef}
                  id="support-subject"
                  type="text"
                  value={contactSubject}
                  onChange={(e) => setContactSubject(e.target.value)}
                  maxLength={CONTACT_SUBJECT_MAX_CHARS}
                  required
                  placeholder="What can we help with?"
                  aria-describedby={handoff ? 'support-handoff-note' : undefined}
                  className="h-11 bg-background"
                  data-testid="support-contact-subject"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="support-message"
                  className="text-sm font-medium text-foreground"
                >
                  Message
                </label>
                <textarea
                  id="support-message"
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  maxLength={CONTACT_MESSAGE_MAX_CHARS}
                  required
                  rows={6}
                  placeholder="Describe your issue or question in detail..."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                  data-testid="support-contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !contactSubject.trim() || !contactMessage.trim()}
                className="mt-2"
                data-testid="support-contact-submit"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
